import { type ComponentProps, forwardRef, type PropsWithChildren, useId, useRef } from 'react'
import type { SizeTokens } from 'tamagui'
import { InputParts } from './inputsParts'
import { useForwardFocus } from './useForwardFocus'
import { StyleSheet, type TextInput } from 'react-native'
import Feather from '@expo/vector-icons/Feather'
import { fontFamilies, mesaColorNames } from '@/src/common/theme'

interface InputProps extends ComponentProps<typeof InputParts.Area> {
  size?: SizeTokens
  leftIcon?: PropsWithChildren['children']
  rightIcon?: PropsWithChildren['children']
  label?: PropsWithChildren['children']
  containerStyle?: ComponentProps<typeof InputParts>['style']
  inputContainerStyle?: ComponentProps<typeof InputParts.Box>['style']
  leftIconContainerStyle?: ComponentProps<typeof InputParts.Icon>['style']
  info?: { props?: ComponentProps<typeof InputParts.Info> } & (
    | { error: string | undefined; success?: never }
    | { success: string | undefined; error?: never }
  )
}

const gap = 12
export { gap as inputGap }

const minHeight = 48
export { minHeight as inputMinHeight }

/**
 * note: make sure to use the same width for the input and the container
 * note: make sure to use the same width for the Input and the Input.Area
 *
 * @see {@link https://tamagui.dev/bento/forms/inputs}
 */
export const Input = forwardRef<TextInput, InputProps>(
  (
    {
      size,
      info,
      leftIcon,
      rightIcon,
      label,
      containerStyle,
      inputContainerStyle,
      leftIconContainerStyle,
      ...props
    },
    ref,
  ) => {
    const nativeUniqueId = useId()
    const uniqueId = props.id || nativeUniqueId
    const inputRef = useRef<TextInput | null>(null)
    const focusTrigger = useForwardFocus(inputRef)

    return (
      <InputParts
        {...(info?.error
          ? { theme: mesaColorNames['semantic/fg/error #DB340B'] }
          : info?.success
            ? { theme: mesaColorNames['semantic/fg/success #008557'] }
            : undefined)}
        // @ts-expect-error tamagui typescript weirdness
        gapScale={0.7}
        size={size}
        style={containerStyle}
      >
        {label ? (
          <InputParts.Label
            htmlFor={uniqueId}
            style={{
              fontFamily: fontFamilies['PlusJakartaSans-Regular'],
              fontWeight: 400,
              fontSize: 16,
              color: mesaColorNames['Greyscale/400 #A4AAAC'],
            }}
          >
            {label}
          </InputParts.Label>
        ) : null}
        <InputParts.Box
          borderBottomColor={mesaColorNames['Greyscale/300 #CDD4D5']}
          borderRightColor={mesaColorNames['Greyscale/300 #CDD4D5']}
          borderTopColor={mesaColorNames['Greyscale/300 #CDD4D5']}
          borderLeftColor={mesaColorNames['Greyscale/300 #CDD4D5']}
          style={StyleSheet.flatten([
            { paddingLeft: 0, paddingRight: 0, backgroundColor: 'transparent', minHeight },
            StyleSheet.flatten([inputContainerStyle]),
          ])}
          animation="quick"
          hoverStyle={{
            borderTopColor: mesaColorNames['Greyscale/500 #6E7375'],
            borderRightColor: mesaColorNames['Greyscale/500 #6E7375'],
            borderBottomColor: mesaColorNames['Greyscale/500 #6E7375'],
            borderLeftColor: mesaColorNames['Greyscale/500 #6E7375'],
          }}
          focusStyle={{
            borderTopColor: mesaColorNames['Greyscale/400 #A4AAAC'],
            borderRightColor: mesaColorNames['Greyscale/400 #A4AAAC'],
            borderBottomColor: mesaColorNames['Greyscale/400 #A4AAAC'],
            borderLeftColor: mesaColorNames['Greyscale/400 #A4AAAC'],
          }}
          tabIndex={-1}
        >
          {info?.error ? (
            <InputParts.Icon
              {...focusTrigger}
              style={[styles.iconContainer, leftIconContainerStyle]}
              tabIndex={-1}
            >
              <Feather
                name="alert-octagon"
                size={24}
                color={mesaColorNames['semantic/fg/error #DB340B']}
              />
            </InputParts.Icon>
          ) : info?.success ? (
            <InputParts.Icon
              {...focusTrigger}
              style={[styles.iconContainer, leftIconContainerStyle]}
              tabIndex={-1}
            >
              <Feather
                name="check"
                size={24}
                color={mesaColorNames['semantic/fg/success #008557']}
              />
            </InputParts.Icon>
          ) : leftIcon ? (
            <InputParts.Icon
              {...focusTrigger}
              style={[styles.iconContainer, leftIconContainerStyle]}
              tabIndex={-1}
            >
              {leftIcon}
            </InputParts.Icon>
          ) : null}

          <InputParts.Area
            tabIndex={0}
            ref={(r) => {
              inputRef.current = r
              typeof ref === 'function' ? ref(r) : ref && (ref.current = r)
            }}
            id={uniqueId}
            {...props}
            style={StyleSheet.flatten([
              {
                fontFamily: fontFamilies['PlusJakartaSans-Regular'],
                fontWeight: 400,
                fontSize: 16,
                color: mesaColorNames['Greyscale/600 #505D64'],
                paddingLeft: gap,
                paddingRight: gap,
                borderTopWidth: 0,
                borderBottomWidth: 0,
                borderLeftWidth: 0,
                borderRightWidth: 0,
                outlineWidth: 0,
                backgroundColor: 'transparent',
                minHeight: minHeight - 2,
              },
              props.style,
            ])}
          />
          {rightIcon ? (
            <InputParts.Icon
              {...focusTrigger}
              style={[styles.iconContainer, { paddingRight: gap }]}
              tabIndex={-1}
            >
              {rightIcon}
            </InputParts.Icon>
          ) : null}
        </InputParts.Box>
        {info?.error ? (
          <InputParts.Info
            {...info.props}
            style={[{ color: mesaColorNames['semantic/fg/error #DB340B'] }, info.props?.style]}
          >
            {info.error}
          </InputParts.Info>
        ) : info?.success ? (
          <InputParts.Info
            {...info.props}
            style={[{ color: mesaColorNames['semantic/fg/success #008557'] }, info.props?.style]}
          >
            {info.success}
          </InputParts.Info>
        ) : null}
      </InputParts>
    )
  },
)

const styles = StyleSheet.create({
  iconContainer: {
    backgroundColor: 'transparent',
    paddingLeft: gap,
    paddingRight: 0,
  },
})
